/**
 * <kinstone-video-button> — master video button component with canvas luma keying,
 * dynamic Theme Editor attribute synchronization, and unified play modes.
 */
class KinstoneVideoButton extends HTMLElement {
  static get observedAttributes() {
    return [
      'src',
      'href',
      'label',
      'width',
      'loop',
      'show-overlay',
      'text-color',
      'text-size',
      'start-time',
      'end-time',
      'play-mode',
      'trigger-scroll',
      'trigger-frame',
      'low',
      'high'
    ];
  }

  readConfig() {
    this.href = this.getAttribute('href') || '#';
    this.src = this.getAttribute('src');
    this.label = this.getAttribute('label') || '';
    this.widthVal = this.getAttribute('width') || '240';
    this.isLoop = this.getAttribute('loop') !== 'false';
    this.showOverlay = this.getAttribute('show-overlay') === 'true';
    this.textColor = this.getAttribute('text-color') || '#ffffff';
    this.textSize = this.getAttribute('text-size') || '11';

    this.idleStart = parseFloat(this.getAttribute('start-time') || '0.1');
    this.idlePause = parseFloat(this.getAttribute('end-time') || '0');
    this.playMode = this.getAttribute('play-mode') || 'scroll_frame';
    this.triggerScroll = parseFloat(this.getAttribute('trigger-scroll') || '0') / 100;
    this.triggerFrame = parseInt(this.getAttribute('trigger-frame') || '0', 10);
    this.low = parseInt(this.getAttribute('low') || '10', 10);
    this.high = parseInt(this.getAttribute('high') || '56', 10);
    this.isScrollTrigger = this.playMode === 'scroll_trigger' || this.playMode === 'scroll_frame' || this.playMode === 'scroll_pct';
  }

  connectedCallback() {
    if (this.dataset.mounted) return;
    this.dataset.mounted = '1';

    this.readConfig();
    if (!this.src) return;

    this.style.display = 'block';

    const link = document.createElement('a');
    link.href = this.href;
    link.setAttribute('aria-label', this.label);
    link.style.cssText =
      'display:block;position:relative;line-height:0;width:' + this.widthVal + 'px;max-width:100%;cursor:pointer;' +
      'transition:transform .35s cubic-bezier(0.16, 1, 0.3, 1);';
    if (this.getAttribute('target')) link.target = this.getAttribute('target');
    this.link = link;

    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'width:100%;height:auto;display:block;pointer-events:none;';
    this.canvas = canvas;
    link.appendChild(canvas);

    // Text Label
    const text = document.createElement('div');
    this.textEl = text;
    this.updateTextDisplay();
    link.appendChild(text);
    this.appendChild(link);

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.setAttribute('crossorigin', 'anonymous');
    video.src = this.src;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = false;
    video.autoplay = false;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.preload = 'auto';
    this.video = video;

    /* ---- helpers ---- */
    const seekSafe = (t) => { try { video.currentTime = t; } catch (e) {} };

    this.stopLoop = () => {
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    };

    this.paintIdle = () => {
      this.stopLoop();
      video.pause();
      seekSafe(this.idleStart);
      this.paint(canvas, video);
    };

    this.beginPlay = () => {
      this.stopLoop();
      if (video.paused) {
        if (video.currentTime < this.idleStart || (this.idlePause > 0 && video.currentTime >= this.idlePause)) {
          seekSafe(this.idleStart);
        }
        video.play().catch(() => {});
      }
      this.loop(canvas, video);
    };

    this.shouldPlayNow = () => {
      if (this.playMode === 'autoplay_loop' || this.playMode === 'autoplay_once') return true;
      if (this.isScrollTrigger && this.triggered) return true;
      return false;
    };

    /* ---- video events ---- */
    video.addEventListener('loadeddata', () => {
      if (this.shouldPlayNow()) this.beginPlay();
      else this.paintIdle();
    });
    video.addEventListener('seeked', () => this.paint(canvas, video));
    video.addEventListener('canplay', () => {
      if (this.shouldPlayNow()) this.beginPlay();
      else this.paintIdle();
    });

    video.addEventListener('ended', () => {
      if (this.playMode === 'autoplay_loop' || (this.isScrollTrigger && this.triggered && this.isLoop)) {
        seekSafe(this.idleStart);
        video.play().catch(() => {});
      } else {
        this.stopLoop();
      }
    });

    video.addEventListener('timeupdate', () => {
      if (this.idlePause > 0 && video.currentTime >= this.idlePause) {
        if (this.playMode === 'autoplay_loop' || (this.isScrollTrigger && this.triggered && this.isLoop)) {
          seekSafe(this.idleStart);
          video.play().catch(() => {});
        } else {
          video.pause();
          seekSafe(this.idlePause);
          this.stopLoop();
        }
      }
    });

    /* ---- hover mode ---- */
    link.addEventListener('mouseenter', () => {
      link.style.transform = 'scale(1.06)';
      if (this.playMode === 'hover_play') {
        this.beginPlay();
      }
    });
    link.addEventListener('mouseleave', () => {
      link.style.transform = 'none';
      if (this.playMode === 'hover_play') {
        this.paintIdle();
      }
    });

    /* ---- scroll trigger modes ---- */
    this.bindHeroFrameListener();

    /* ---- intersection observer ---- */
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (this.shouldPlayNow()) this.beginPlay();
        else this.paintIdle();
      } else {
        video.pause();
        this.stopLoop();
      }
    });
    this.observer.observe(this);

    // Initial status check
    if (this.shouldPlayNow()) this.beginPlay();
    else this.paintIdle();
  }

  bindHeroFrameListener() {
    if (this._onHeroFrame) {
      document.removeEventListener('kinstone:frame', this._onHeroFrame);
      this._onHeroFrame = null;
    }

    if (this.isScrollTrigger) {
      this._onHeroFrame = (e) => {
        const frac = e.detail && e.detail.fraction;
        const frameIdx = e.detail && e.detail.index;
        if (frac === undefined) return;

        let atTrigger = false;
        let belowTrigger = false;

        if (this.playMode === 'scroll_frame' || (this.playMode === 'scroll_trigger' && this.triggerFrame > 0)) {
          atTrigger = frameIdx !== undefined && (frameIdx + 1 >= this.triggerFrame);
          belowTrigger = frameIdx !== undefined && (frameIdx + 1 < this.triggerFrame);
        } else {
          atTrigger = frac >= this.triggerScroll;
          belowTrigger = frac < (this.triggerScroll - 0.02);
        }

        if (!this.triggered && atTrigger) {
          this.triggered = true;
          this.beginPlay();
        } else if (this.triggered && belowTrigger) {
          this.triggered = false;
          this.paintIdle();
        }
      };
      document.addEventListener('kinstone:frame', this._onHeroFrame);
    }
  }

  updateTextDisplay() {
    if (!this.textEl) return;
    if (this.showOverlay && this.label.trim() !== '') {
      this.textEl.className = 'kvb__overlay-text';
      this.textEl.innerHTML = '<span>' + this.label.replace(/\n/g, '<br>') + '</span><span style="display:block;margin-top:4px;font-size:1.2em">&#8594;</span>';
      this.textEl.style.cssText =
        'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;' +
        'justify-content:center;text-align:center;color:' + this.textColor + ';' +
        'font-family:var(--k-display, "Cinzel", serif);font-size:' + this.textSize + 'px;' +
        'letter-spacing:0.22em;text-transform:uppercase;pointer-events:none;z-index:2;' +
        'text-shadow:0 1px 5px rgba(0,0,0,0.85);line-height:1.3;padding:12px;';
    } else {
      this.textEl.textContent = this.label;
      this.textEl.style.cssText =
        'position:absolute;width:1px;height:1px;margin:-1px;overflow:hidden;' +
        'clip:rect(0 0 0 0);white-space:nowrap';
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.dataset.mounted) return;
    this.readConfig();

    if (this.link) {
      this.link.href = this.href;
      this.link.style.width = this.widthVal + 'px';
      this.updateTextDisplay();
    }

    if (this.video && name === 'src' && this.video.src !== this.src) {
      this.video.src = this.src;
    }

    this.triggered = false;
    this.bindHeroFrameListener();

    if (this.shouldPlayNow && this.beginPlay && this.paintIdle) {
      if (this.shouldPlayNow()) this.beginPlay();
      else this.paintIdle();
    }
  }

  disconnectedCallback() {
    if (this.stopLoop) this.stopLoop();
    if (this.observer) this.observer.disconnect();
    if (this.video) this.video.pause();
    if (this._onHeroFrame) document.removeEventListener('kinstone:frame', this._onHeroFrame);
  }

  loop(canvas, video) {
    this.raf = requestAnimationFrame(() => this.loop(canvas, video));
    this.paint(canvas, video);
  }

  paint(canvas, video) {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh || video.readyState < 2) return;

    if (canvas.width !== vw || canvas.height !== vh) {
      canvas.width = vw;
      canvas.height = vh;
      this.scratch = document.createElement('canvas');
      this.scratch.width = vw;
      this.scratch.height = vh;
    }

    const sctx = this.scratch.getContext('2d', { willReadFrequently: true });
    sctx.drawImage(video, 0, 0, vw, vh);

    try {
      const frame = sctx.getImageData(0, 0, vw, vh);
      const d = frame.data;
      const low = this.low;
      const high = this.high;
      const span = Math.max(1, high - low);

      for (let i = 0; i < d.length; i += 4) {
        const r = d[i], g = d[i + 1], b = d[i + 2];
        const lum = r > g ? (r > b ? r : b) : g > b ? g : b;
        d[i + 3] = lum <= low ? 0 : lum >= high ? 255 : ((lum - low) / span) * 255;
      }

      canvas.getContext('2d').putImageData(frame, 0, 0);
    } catch (e) {
      canvas.getContext('2d').drawImage(video, 0, 0, vw, vh);
    }
  }
}

if (!customElements.get('kinstone-video-button')) {
  customElements.define('kinstone-video-button', KinstoneVideoButton);
}
